import React, { useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { LMS_SUPPORTED_LANGUAGES, getLmsTranslationField } from '../../lookup-definitions/constants/lmsLanguages';

const TEXT_COLUMN_MAX_LENGTH = 40;

/**
 * Ön Koşul Tanımları listesi. `code` sütunu salt okunur bilgi amaçlıdır (backend üretir,
 * hiçbir ekrandan düzenlenmez) — bkz. doc §6.6.
 */
const PreconditionDefinitionsTable = ({ data, loading, onPage, lazyParams, totalCount, onEdit }) => {
    const { th } = useLanguage();
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }, [data]);

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return normalizedData;

        return [...normalizedData].sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortOrder;
            if (bValue == null) return -sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortField.toLowerCase().includes('date')) {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -sortOrder;
            if (aValue > bValue) return sortOrder;
            return 0;
        });
    }, [normalizedData, sortField, sortOrder]);

    const handleSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')} onClick={() => onEdit(rowData)} />
        </div>
    );

    return (
        <DataTable
            className="p-datatable-gridlines"
            value={sortedData}
            paginator
            rows={lazyParams.rows || 10}
            loading={loading}
            totalRecords={totalCount}
            showGridlines
            lazy
            scrollable
            scrollDirection="horizontal"
            first={lazyParams.first}
            onPage={onPage}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            dataKey="id"
            emptyMessage={th('preconditionDefinitions.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            tableStyle={{ minWidth: '110rem' }}
            sortMode="single"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <Column body={actionBodyTemplate} style={{ width: '4rem' }} align="center" frozen />
            <Column sortable field="code" header={th('preconditionDefinitions.columnCode')} style={{ width: '8rem' }} frozen />
            {LMS_SUPPORTED_LANGUAGES.map((lang) => (
                <Column key={lang.id} header={lang.name} style={{ width: '14rem' }} body={(row) => createTruncatedTextForTable(getLmsTranslationField(row.translations, lang.id, 'precondition'), TEXT_COLUMN_MAX_LENGTH)} />
            ))}
            <Column sortable field="createdDate" header={th('preconditionDefinitions.columnCreatedDate')} style={{ width: '10rem' }} body={(row) => dateOnlyBodyTemplate(row, 'createdDate')} />
            <Column sortable field="createdName" header={th('preconditionDefinitions.columnCreatedName')} style={{ width: '10rem' }} />
        </DataTable>
    );
};

export default PreconditionDefinitionsTable;
